import { useCallback, useRef, useState } from 'react';

import useFetch from 'hooks/useFetch';
import usePagination from 'hooks/usePagination';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import {
    CHEMICALS_SEARCH_API,
    CHEMICAL_DB_API_HOST,
    DEFAULT_PAGE_SIZE,
    JSON_HEADERS
} from './constants';
import type {
    ChemicalSearchResult,
    PagedResponse,
    PaginatedNextFn,
    PaginatedSearchResponse,
    Search,
    SearchParams
} from './types';

const MIN_QUERY_LENGTH = 2;
const FIRST_PAGE = 1;

const useChemicalSearch = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const queryRef = useRef<string>('');
    // Count in-flight requests so rapid typing (which spawns overlapping
    // searches) doesn't let an earlier search's finally clear isLoading while
    // a later search is still pending — otherwise "No results." flashes.
    const inFlightCount = useRef<number>(0);
    const { authenticatedGet } = useFetch({
        apiHost: CHEMICAL_DB_API_HOST,
        headers: JSON_HEADERS
    });
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR } = useTranslation();

    const beginRequest = () => {
        inFlightCount.current += 1;
        setIsLoading(true);
    };

    const endRequest = () => {
        inFlightCount.current = Math.max(0, inFlightCount.current - 1);

        if (inFlightCount.current === 0) {
            setIsLoading(false);
        }
    };

    const callback = async ({
        pageToken
    }: {
        pageToken?: string;
    }): Promise<PagedResponse<ChemicalSearchResult> | null> => {
        const trimmedQuery = queryRef.current.trim();

        if (trimmedQuery.length < MIN_QUERY_LENGTH) {
            return null;
        }

        const page = Number(pageToken ?? String(FIRST_PAGE));
        const url = `${CHEMICALS_SEARCH_API}?q=${encodeURIComponent(trimmedQuery)}&page=${page}&pageSize=${DEFAULT_PAGE_SIZE}`;

        // isLoading is managed by the outer search/fetch wrappers so it stays
        // true until setData commits — otherwise a flash render can show
        // "No results." between the callback resolving and pagination's setData.
        const response = await withRetry(
            () => authenticatedGet<PaginatedSearchResponse>(url),
            CONNECTION_ERROR,
            async () => {
                await callback({ pageToken });

                return null;
            }
        );

        if (!response) {
            return null;
        }

        if (queryRef.current.trim() !== trimmedQuery) {
            return null;
        }

        return {
            nextPageToken: response.pagination.hasNext ? String(page + 1) : undefined,
            objects: response.results,
            totalCount: response.pagination.totalCount
        };
    };

    const {
        data,
        fetch: paginatedFetch,
        reset,
        totalCount
    } = usePagination<ChemicalSearchResult>({ callback });

    // Wrap the generator so consumers keep `fetch().next()` ergonomics while
    // isLoading brackets the whole next-page lifecycle (including setData).
    const fetch = useCallback((): { next: PaginatedNextFn<ChemicalSearchResult> } => {
        const generator = paginatedFetch({});

        return {
            next: async () => {
                beginRequest();

                try {
                    return await generator.next();
                } finally {
                    endRequest();
                }
            }
        };
    }, [paginatedFetch]);

    const search: Search = useCallback(
        async ({ query }: SearchParams) => {
            beginRequest();
            queryRef.current = query;
            reset();

            try {
                await paginatedFetch({}).next();
            } finally {
                endRequest();
            }
        },
        [paginatedFetch, reset]
    );

    return {
        data,
        fetch,
        isLoading,
        reset,
        results: (data ?? []) as ChemicalSearchResult[],
        search,
        totalCount
    };
};

export default useChemicalSearch;
